/**
 * Workspace · Petty Cash anchor (S33) — the drawer detail page.
 *
 * Composes Phase 1b feature components against two tRPC reads:
 *   - `pettyCash.anchorData` — drawer + this-month rollups + recent counts +
 *     awaiting-founder-review count.
 *   - `cashMovements.list` — activity stream for the drawer (filtered to the
 *     drawer's `accountId`, fires only after anchorData resolves so we
 *     have the drawer id).
 *
 * Layout follows `prototype/cash/petty-cash.html`:
 *   <DrawerHeader />
 *   2-col body: LEFT activity stream + filters · RIGHT sidebar cards
 *   (drawer balance + ImprestHealthCard + RecentCountsCard + drawer details
 *    + this-month-at-a-glance + V1.x cash-trend placeholder).
 *
 * @see prototype/cash/petty-cash.html
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Check, RefreshCcw, Search } from "lucide-react";
import { Alert, Card, CardBody, CardHeader, Input, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell, V1xPlaceholderCard } from "../../../../features/workspace/index.ts";
import { ListPagination } from "../../../../features/master-list/index.ts";
import {
  DrawerHeader,
  ImprestHealthCard,
  RecentCountsCard,
  VoucherActivityRow,
  type PettyCashCategory,
} from "../../../../features/petty-cash/index.ts";
import { paiseToInr, formatDate } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/cash/petty-cash/")({
  component: PettyCashAnchorPage,
});

function PettyCashAnchorPage() {
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});

  const drawerId = anchorQ.data?.drawer.id;
  const activityQ = trpc.cashMovements.list.useQuery(
    { bankAccountId: drawerId ?? "", limit: 50 },
    { enabled: !!drawerId },
  );

  const isLoading = anchorQ.isLoading;
  const firstError = anchorQ.error ?? activityQ.error;

  if (isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Petty cash" }]}>
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading petty cash drawer…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }

  if (firstError) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Petty cash" }]}>
        <Alert
          intent="bad"
          title="Couldn't load petty cash drawer"
          description={firstError.message}
        />
      </WorkspaceShell>
    );
  }

  const anchor = anchorQ.data;
  if (!anchor) return null;

  const drawer = anchor.drawer;
  const thisMonth = anchor.thisMonth;
  const recentCounts = anchor.recentCounts;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Petty cash" }]}>
      <div className="flex flex-col">
        <DrawerHeader
          name={drawer.name}
          imprestPaise={drawer.imprestPaise}
          balancePaise={drawer.balancePaise}
          daysSinceLastCount={drawer.daysSinceLastCount}
          custodian={drawer.custodian}
          operator={drawer.operator}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-5">
          {/* LEFT — activity stream */}
          <section className="flex flex-col gap-4 min-w-0">
            <ActivitySection
              rows={activityQ.data?.rows ?? []}
              loading={activityQ.isLoading}
              error={activityQ.error?.message ?? null}
              drawerId={drawerId}
            />

            <V1xPlaceholderCard
              title="30-day cash trend chart"
              description="Sparkline of drawer balance + replenishment markers. Lands in V1.x once the time-series aggregator ships."
            />
          </section>

          {/* RIGHT — sidebar */}
          <aside className="flex flex-col gap-4">
            <DrawerBalanceCard
              balancePaise={drawer.balancePaise}
              imprestPaise={drawer.imprestPaise}
              lastCountedAt={drawer.lastCountedAt}
            />
            <ImprestHealthCard
              imprestPaise={drawer.imprestPaise}
              balancePaise={drawer.balancePaise}
            />
            <RecentCountsCard counts={recentCounts} />
            <DrawerDetailsCard
              imprestPaise={drawer.imprestPaise}
              custodian={drawer.custodian}
              operator={drawer.operator}
            />
            <ThisMonthCard
              voucherCount={thisMonth.voucherCount}
              totalSpentPaise={thisMonth.totalSpentPaise}
              topCategory={thisMonth.topCategory}
              awaitingFounderReview={anchor.awaitingFounderReview}
            />
          </aside>
        </div>
      </div>
    </WorkspaceShell>
  );
}
// ── Activity stream ─────────────────────────────────────────────────

interface ActivityRow {
  id: string;
  kind: string;
  fromAccountId: string | null;
  fromAccountName: string | null;
  toAccountId: string | null;
  toAccountName: string | null;
  amountPaise: string;
  valueDate: string;
  narration: string;
  /** From `cash_movements.expense_category`; only populated for `petty_cash_expense`. */
  expenseCategory: PettyCashCategory | null;
  createdAt: string;
}

type ActivityFilter = "all" | "expense" | "replenish" | "count";

function ActivitySection({
  rows,
  loading,
  error,
  drawerId,
}: {
  rows: ReadonlyArray<ActivityRow>;
  loading: boolean;
  error: string | null;
  drawerId: string | undefined;
}) {
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => filterActivity(rows, filter, query), [rows, filter, query]);
  const grouped = useMemo(() => groupByDay(filtered, drawerId), [filtered, drawerId]);
  const stats = useMemo(() => computeActivityStats(filtered, drawerId), [filtered, drawerId]);

  return (
    <>
      {/* Filter tabs — shared @flint/ui Tabs primitive (brand-spec §13.2). */}
      <TabsRoot value={filter} onValueChange={(v) => setFilter(v as ActivityFilter)}>
        <TabsList aria-label="Activity filter">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="expense">
            <ArrowUp size={13} strokeWidth={2.2} aria-hidden />
            Vouchers
          </TabsTrigger>
          <TabsTrigger value="replenish">
            <ArrowDown size={13} strokeWidth={2.2} aria-hidden />
            Replenishments
          </TabsTrigger>
          <TabsTrigger value="count">
            <Check size={13} strokeWidth={2.2} aria-hidden />
            Counts
          </TabsTrigger>
        </TabsList>
      </TabsRoot>

      {/* Filter bar — search at standard h-10 size + event count, mirrors
          BillFilterBar (apps/web/src/features/bills/list/bill-filter-bar.tsx). */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative h-10 min-w-80 flex-1 lg:max-w-[38rem]">
          <Search
            size={15}
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <Input
            type="search"
            aria-label="Search activity"
            placeholder="Search activity by narration, account name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-10 pl-9"
          />
        </div>
        <span className="text-xs text-muted ml-auto">
          {filtered.length} {filtered.length === 1 ? "event" : "events"}
        </span>
      </div>

      {/* Activity card */}
      <Card>
        <CardBody className="!p-0">
          {loading ? (
            <p className="px-4 py-6 text-sm text-muted text-center">Loading activity…</p>
          ) : error ? (
            <Alert intent="bad" title="Couldn't load activity" description={error} />
          ) : grouped.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-semibold text-ink-2">No activity yet</p>
              <p className="text-xs text-muted mt-1">
                Vouchers, replenishments, and counts on this drawer will appear here.
              </p>
            </div>
          ) : (
            grouped.map((day) => (
              <div key={day.date}>
                <div className="px-4 py-2 bg-raised border-y border-line flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink-2">
                    {formatLongDate(day.date)}
                  </span>
                  <span className="text-xs text-muted">
                    Net{" "}
                    <span
                      className={cn(
                        day.netPaise >= 0n ? "text-ok" : "text-bad",
                        "num font-semibold",
                      )}
                    >
                      {formatRupeesSigned(day.netPaise)}
                    </span>
                  </span>
                </div>
                {day.rows.map((r) => renderActivityRow(r, drawerId))}
              </div>
            ))
          )}
        </CardBody>
        {/* Footer 1 — visible-set stats strip. Mirrors MasterListCard's
            `.list-foot`: bg-raised, text-xs muted, mono bold values,
            thin h-3 dividers. Only when there are rows. */}
        {!loading && !error && filtered.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 bg-raised border-t border-line text-xs text-muted">
            <span className="inline-flex items-center gap-1.5">
              Entries
              <strong className="font-mono tabular-nums font-bold text-ink">
                {stats.count}
              </strong>
            </span>
            <span aria-hidden className="h-3 w-px bg-line-2" />
            <span className="inline-flex items-center gap-1.5">
              Total out
              <strong className="font-mono tabular-nums font-bold text-bad">
                {paiseToInr(stats.totalOutPaise.toString())}
              </strong>
            </span>
            <span aria-hidden className="h-3 w-px bg-line-2" />
            <span className="inline-flex items-center gap-1.5">
              Avg out
              <strong className="font-mono tabular-nums font-bold text-ink">
                {paiseToInr(stats.avgOutPaise.toString())}
              </strong>
            </span>
            {stats.dateRange ? (
              <>
                <span aria-hidden className="h-3 w-px bg-line-2" />
                <span className="inline-flex items-center gap-1.5">
                  Range
                  <strong className="font-mono tabular-nums font-bold text-ink">
                    {stats.dateRange}
                  </strong>
                </span>
              </>
            ) : null}
          </div>
        ) : null}
        {/* Footer 2 — static pagination strip (activity is a bounded recent
            set; no cursor). */}
        {!loading && !error && filtered.length > 0 ? (
          <div className="border-t border-line">
            <ListPagination
              pageNumber={1}
              showingFrom={filtered.length ? 1 : 0}
              showingTo={filtered.length}
              total={filtered.length}
              sortLabel="most recent first"
              hasPrev={false}
              hasNext={false}
              onPrev={() => {}}
              onNext={() => {}}
            />
          </div>
        ) : null}
      </Card>
    </>
  );
}

/**
 * Visible-set aggregates for the activity footer.
 *
 * "Total out" sums the outflow side (expenses + transfers/counts where the
 * drawer is the source) using the same drawer-relative sign as the day-net
 * rollup; inflows (replenishments) don't count toward spend. `avgOut` is
 * over the number of outflow entries.
 */
function computeActivityStats(
  rows: ReadonlyArray<ActivityRow>,
  drawerId: string | undefined,
): {
  count: number;
  totalOutPaise: bigint;
  avgOutPaise: bigint;
  dateRange: string | null;
} {
  if (rows.length === 0) {
    return { count: 0, totalOutPaise: 0n, avgOutPaise: 0n, dateRange: null };
  }
  let totalOut = 0n;
  let outCount = 0;
  let min = rows[0]!.valueDate;
  let max = rows[0]!.valueDate;
  for (const r of rows) {
    const signed = signedAmount(r, drawerId);
    if (signed < 0n) {
      totalOut += -signed;
      outCount += 1;
    }
    if (r.valueDate < min) min = r.valueDate;
    if (r.valueDate > max) max = r.valueDate;
  }
  return {
    count: rows.length,
    totalOutPaise: totalOut,
    avgOutPaise: outCount > 0 ? totalOut / BigInt(outCount) : 0n,
    dateRange: min === max ? formatDate(min) : `${formatDate(min)} → ${formatDate(max)}`,
  };
}

function renderActivityRow(row: ActivityRow, drawerId: string | undefined) {
  // Expense voucher → use the polished VoucherActivityRow.
  if (row.kind === "petty_cash_expense") {
    // `expenseCategory` is the wire value from `cash_movements.expense_category`.
    // The domain ensures it's set for kind=petty_cash_expense; fall back to
    // 'sundries' defensively for old rows that pre-date the contract widening.
    const category: PettyCashCategory = row.expenseCategory ?? "sundries";
    const time = formatTime(row.createdAt);
    return (
      <VoucherActivityRow
        key={row.id}
        id={row.id}
        time={time}
        category={category}
        narration={row.narration}
        amountPaise={row.amountPaise}
        postedBy="Operator"
        requiresFounderReview={false}
      />
    );
  }
  // Replenishment / count / banked-cash → simpler row (mirrors
  // `.act-row.s-emerald` / `s-violet`). The drawer can sit on either
  // side of a transfer or count adjustment, so the row needs to know
  // which side it's on to flip the sign.
  return <ActivityFallbackRow key={row.id} row={row} drawerId={drawerId} />;
}

function ActivityFallbackRow({
  row,
  drawerId,
}: {
  row: ActivityRow;
  drawerId: string | undefined;
}) {
  const isTransfer = row.kind === "transfer";
  const isCount = row.kind === "cash_count_adjustment";
  const time = formatTime(row.createdAt);
  // Direction relative to THIS drawer. The drawer is either the source
  // (outflow) or the destination (inflow). Default to "outflow" when
  // drawerId is null (defensive) so a transfer with no resolved drawer
  // still renders sensibly.
  const isInflow = drawerId !== undefined && row.toAccountId === drawerId;
  const isOutflow = !isInflow;
  const counterpartyName = isInflow ? row.fromAccountName : row.toAccountName;
  const label = isTransfer
    ? isInflow
      ? `Replenishment from ${counterpartyName ?? "bank"}`
      : `Banked to ${counterpartyName ?? "bank"}`
    : isCount
      ? isInflow
        ? "Cash count · over"
        : "Cash count · short"
      : row.narration;
  // Transfers go to /transfers/$movementId; everything else (counts +
  // adjustments) goes to the generic voucher detail under /petty-cash.
  return (
    <Link
      to={isTransfer ? "/cash/transfers/$movementId" : "/cash/petty-cash/$voucherId"}
      params={isTransfer ? { movementId: row.id } : { voucherId: row.id }}
      className="grid grid-cols-[28px_minmax(0,1fr)_auto] gap-3 px-4 py-3 border-b border-line last:border-b-0 items-center hover:bg-raised transition-colors no-underline text-inherit"
    >
      <span
        aria-hidden
        className={cn(
          "inline-grid place-items-center w-7 h-7 rounded-md shrink-0",
          isTransfer
            ? isInflow
              ? "bg-ok-50 text-ok"
              : "bg-bad-50 text-bad"
            : isCount
              ? "bg-sky-50 text-sky-700"
              : "bg-raised text-muted",
        )}
      >
        {isTransfer ? (
          isInflow ? (
            <ArrowDown size={13} strokeWidth={2} />
          ) : (
            <ArrowUp size={13} strokeWidth={2} />
          )
        ) : isCount ? (
          <Check size={13} strokeWidth={2} />
        ) : (
          <RefreshCcw size={13} strokeWidth={2} />
        )}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink truncate">{label}</p>
        <p className="text-xs text-muted truncate">
          {row.narration} · {time}
        </p>
      </div>
      <div className="text-right">
        <p
          className={cn(
            "num tabular-nums text-sm font-semibold whitespace-nowrap",
            isInflow ? "text-ok" : isOutflow ? "text-bad" : "text-ink-2",
          )}
        >
          {isInflow ? "+" : isOutflow ? "−" : ""}
          {paiseToInr(row.amountPaise)}
        </p>
        <p className="text-xs text-muted mt-0.5">{time}</p>
      </div>
    </Link>
  );
}

/**
 * Bottom-border tab — same pattern as features/invoices/list/status-tabs.tsx
 * and features/bills/list/bill-status-tabs.tsx so the filter UI on
 * /petty-cash matches the standard list-page chrome elsewhere in the
 * app. h-42 effective, border-b-2 active rail in sky-600.
 */
// ── Sidebar cards ────────────────────────────────────────────────────

function DrawerBalanceCard({
  balancePaise,
  imprestPaise,
  lastCountedAt,
}: {
  balancePaise: string;
  imprestPaise: string;
  lastCountedAt: string | null;
}) {
  const balance = BigInt(balancePaise);
  const imprest = BigInt(imprestPaise);
  const reimburseToTopUp = imprest > balance ? imprest - balance : 0n;

  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wider text-muted font-semibold">Drawer balance</p>
        <p className="text-3xl font-bold tracking-tight num leading-none mt-1 mb-3">
          {paiseToInr(balance)}
        </p>
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-muted">Imprest ceiling</span>
            <span className="num font-semibold">{paiseToInr(imprest)}</span>
          </div>
          {reimburseToTopUp > 0n ? (
            <div className="flex items-baseline justify-between">
              <span className="text-muted">Reimburse to top up</span>
              <span className="num font-semibold text-sky-700">{paiseToInr(reimburseToTopUp)}</span>
            </div>
          ) : null}
        </div>
        {lastCountedAt ? (
          <p className="mt-3 px-2.5 py-2 bg-ok-50 border border-ok-100 rounded-sm text-xs text-ok flex items-center gap-1.5">
            <Check size={11} strokeWidth={3} aria-hidden />
            Last counted: {formatDate(lastCountedAt.slice(0, 10))}
          </p>
        ) : (
          <p className="mt-3 px-2.5 py-2 bg-warn-50 border border-warn-100 rounded-sm text-xs text-warn">
            No physical count on record yet — run one to seed the audit trail.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function DrawerDetailsCard({
  imprestPaise,
  custodian,
  operator,
}: {
  imprestPaise: string;
  custodian: string | null;
  operator: string | null;
}) {
  return (
    <Card>
      <CardHeader className="p-0 border-b-0">
        <h3 className="px-4 pt-3.5 text-xs font-semibold text-muted uppercase tracking-wider">
          Drawer details
        </h3>
      </CardHeader>
      <CardBody className="pt-3">
        <dl className="flex flex-col gap-2 text-sm">
          <DetailRow label="Custodian" value={custodian ?? "— (V1.x)"} />
          <DetailRow label="Operator" value={operator ?? "— (V1.x)"} />
          <DetailRow label="Imprest ceiling" value={paiseToInr(imprestPaise)} />
          <DetailRow label="Currency" value="INR" />
          <DetailRow label="Linked" value="ADR-0012 §10" />
        </dl>
      </CardBody>
    </Card>
  );
}

function ThisMonthCard({
  voucherCount,
  totalSpentPaise,
  topCategory,
  awaitingFounderReview,
}: {
  voucherCount: number;
  totalSpentPaise: string;
  topCategory: { category: PettyCashCategory; paise: string; percent: number } | null;
  awaitingFounderReview: number;
}) {
  return (
    <Card>
      <CardHeader className="p-0 border-b-0">
        <h3 className="px-4 pt-3.5 text-xs font-semibold text-muted uppercase tracking-wider">
          This month at a glance
        </h3>
      </CardHeader>
      <CardBody className="pt-3">
        <dl className="flex flex-col gap-2 text-sm">
          <DetailRow label="Vouchers posted" value={voucherCount.toString()} />
          <DetailRow label="Total spent" value={paiseToInr(totalSpentPaise)} />
          <DetailRow
            label="Top category"
            value={
              topCategory
                ? `${prettyCategory(topCategory.category)} · ${paiseToInr(topCategory.paise)} (${topCategory.percent.toFixed(0)}%)`
                : "—"
            }
          />
          <DetailRow
            label="Awaiting founder review"
            value={awaitingFounderReview.toString()}
            tone={awaitingFounderReview > 0 ? "warn" : "ok"}
          />
        </dl>
      </CardBody>
    </Card>
  );
}

function DetailRow({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-dashed border-line last:border-b-0 pb-2 last:pb-0">
      <dt className="text-muted text-xs">{label}</dt>
      <dd
        className={cn(
          "text-sm font-semibold text-right",
          tone === "warn" && "text-warn",
          tone === "ok" && "text-ok",
          !tone && "text-ink-2",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────

function cn(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function filterActivity(
  rows: ReadonlyArray<ActivityRow>,
  filter: ActivityFilter,
  query: string,
): ReadonlyArray<ActivityRow> {
  const q = query.trim().toLowerCase();
  return rows.filter((r) => {
    switch (filter) {
      case "expense":
        if (r.kind !== "petty_cash_expense") return false;
        break;
      case "replenish":
        if (r.kind !== "transfer") return false;
        break;
      case "count":
        if (r.kind !== "cash_count_adjustment") return false;
        break;
      case "all":
      default:
        break;
    }
    if (!q) return true;
    return (
      r.narration.toLowerCase().includes(q) ||
      (r.fromAccountName ?? "").toLowerCase().includes(q) ||
      (r.toAccountName ?? "").toLowerCase().includes(q)
    );
  });
}

function groupByDay(
  rows: ReadonlyArray<ActivityRow>,
  drawerId: string | undefined,
): Array<{
  date: string;
  rows: ActivityRow[];
  netPaise: bigint;
}> {
  const byDay = new Map<string, { rows: ActivityRow[]; netPaise: bigint }>();
  for (const r of rows) {
    const signedPaise = signedAmount(r, drawerId);
    const bucket = byDay.get(r.valueDate);
    if (bucket) {
      bucket.rows.push(r);
      bucket.netPaise += signedPaise;
    } else {
      byDay.set(r.valueDate, { rows: [r], netPaise: signedPaise });
    }
  }
  return Array.from(byDay.entries()).map(([date, v]) => ({
    date,
    rows: v.rows,
    netPaise: v.netPaise,
  }));
}

/**
 * Signed paise for the day-net rollup, relative to the drawer.
 *
 * - petty_cash_expense always exits the drawer → negative.
 * - transfer / cash_count_adjustment can go either way depending on
 *   whether the drawer is `from` (outflow → negative) or `to` (inflow
 *   → positive).
 */
function signedAmount(row: ActivityRow, drawerId: string | undefined): bigint {
  const paise = BigInt(row.amountPaise);
  if (row.kind === "petty_cash_expense") return -paise;
  if (drawerId !== undefined && row.toAccountId === drawerId) return paise;
  return -paise;
}

function formatLongDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const todayIso = new Date().toISOString().slice(0, 10);
  const yIso = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  if (iso === todayIso) return `Today · ${formatDate(iso)}`;
  if (iso === yIso) return `Yesterday · ${formatDate(iso)}`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatRupeesSigned(paise: bigint): string {
  if (paise === 0n) return "₹0";
  const abs = paise < 0n ? -paise : paise;
  const sign = paise < 0n ? "−" : "+";
  return `${sign}${paiseToInr(abs)}`;
}

function prettyCategory(slug: PettyCashCategory): string {
  switch (slug) {
    case "postage":
      return "Postage";
    case "conveyance":
      return "Conveyance";
    case "travel":
      return "Travel";
    case "refreshments":
      return "Refreshments";
    case "office_supplies":
      return "Office supplies";
    case "printing_stationery":
      return "Printing";
    case "repairs_maintenance":
      return "Repairs";
    case "sundries":
      return "Sundries";
  }
}
